<template>
  <div class="min-h-screen bg-gray-100 pb-32">
    <div class="bg-blue-600 text-white px-4 py-4 sticky top-0 z-20 shadow-md">
      <h1 class="text-xl font-bold">巡检报告填报</h1>
    </div>

    <div class="p-4 space-y-4 max-w-2xl mx-auto mb-10">
      <div
        v-for="item in tableData"
        :key="item.id"
        class="bg-white rounded-xl shadow-sm p-4 overflow-hidden border border-gray-100"
      >
        <div
          class="flex items-start justify-between mb-3 border-b pb-2 border-dashed"
        >
          <div class="flex items-center gap-2">
            <span
              class="bg-blue-100 text-blue-600 text-xs font-bold px-2 py-0.5 rounded"
            >
              #{{ item.id }}
            </span>
            <span class="font-bold text-gray-800 text-lg">{{
              item.category
            }}</span>
          </div>
        </div>

        <div
          class="text-gray-600 text-sm mb-4 leading-relaxed bg-gray-50 p-2 rounded whitespace-pre-line text-left"
        >
          {{ item.content }}
        </div>

        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div v-for="photo in photoSlots" :key="photo.field" class="min-w-0">
            <ImageUpload
              v-model="item[photo.field]"
              :cache-key="photoCacheKey(item, photo.field)"
            />
            <div class="text-center text-xs text-gray-400 mt-1">{{ photo.label }}</div>
          </div>
        </div>

        <div v-if="!isPhotoOnlyItem(item)">
          <div class="text-xs text-gray-400 mb-1 ml-1">检查结果</div>
          <el-select
            v-model="item.result"
            placeholder="请选择结果"
            :class="['w-full', getResultSelectClass(item.result)]"
            size="large"
          >
            <el-option v-for="option in item.resultOptions" :key="option" :label="option" :value="option">
              <span :class="getResultSelectClass(option)">{{ option }}</span>
            </el-option>
          </el-select>
        </div>
      </div>

      <div
        class="bg-white rounded-xl shadow-sm p-4 overflow-hidden border border-gray-100"
      >
        <div
          class="flex items-start justify-between mb-3 border-b pb-2 border-dashed"
        >
          <div class="flex items-center gap-2">
            <span
              class="bg-blue-100 text-blue-600 text-xs font-bold px-2 py-0.5 rounded"
            >
              #{{ tableData.length + 1 }}
            </span>
            <span class="font-bold text-gray-800 text-lg">检查结果汇总</span>
          </div>
        </div>

        <div>
          <div class="text-xs text-gray-400 mb-1 ml-1">请输入汇总内容</div>
          <el-input
            v-model="summaryText"
            type="textarea"
            :rows="4"
            resize="vertical"
            placeholder="请输入检查结果汇总"
          />
        </div>
      </div>
    </div>

    <div
      class="fixed bottom-0 left-0 right-0 bg-white border-t p-4 z-30 shadow-[0_-4px_10px_rgba(0,0,0,0.05)]"
    >
      <div class="max-w-2xl mx-auto flex flex-col gap-2">
        <div v-if="progressVisible" class="px-1">
          <el-progress :percentage="currentProgress" :stroke-width="8" />
          <div class="text-xs text-center text-gray-500 mt-1">
            {{ currentStatusText || "处理中..." }}
          </div>
        </div>
        <div class="flex gap-2">
          <el-button
            type="primary"
            size="large"
            class="flex-1 text-lg h-12 font-bold !rounded-xl shadow-lg shadow-blue-200"
            @click="handleExportExcel"
            :loading="exporting && exportingKind === 'excel'"
            :disabled="!templateConfig || backuping || (exporting && exportingKind !== 'excel')"
          >
            导出 Excel 报表
          </el-button>
          <el-button
            type="danger"
            size="large"
            class="flex-1 text-lg h-12 font-bold !rounded-xl shadow-lg shadow-red-200"
            @click="handleExportPdf"
            :loading="exporting && exportingKind === 'pdf'"
            :disabled="!templateConfig || backuping || (exporting && exportingKind !== 'pdf')"
          >
            导出 PDF
          </el-button>
        </div>
        <div class="flex gap-2">
          <el-button
            size="large"
            class="flex-1 !rounded-xl"
            @click="handleExportBackup"
            :disabled="!templateConfig || anyBusy"
          >
            导出数据
          </el-button>
          <el-button
            size="large"
            class="flex-1 !rounded-xl"
            @click="triggerImportBackup"
            :disabled="!templateConfig || anyBusy"
          >
            导入数据
          </el-button>
        </div>
        <input
          ref="backupFileInput"
          type="file"
          accept="application/json"
          class="hidden"
          @change="handleImportBackupFile"
        />
        <div class="text-xs text-center mt-2 select-none">
          ©
          <a href="mailto:vanderwals@163.com" class="text-blue-500"
            >vanderwals@163.com</a
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";
import ExcelJS from "exceljs";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { jsPDF } from "jspdf";
import ImageUpload from "./ImageUpload.vue";
import {
  DB_NAME,
  DB_VERSION,
  STORE_NAME,
  idbEntries,
  idbSet,
  idbDel,
} from "../utils/watermarkIdb";

// 数据源拆出，便于维护和复用 data.json
const originalData = ref([]);
const templateConfig = ref(null);
const photoSlots = computed(() =>
  (templateConfig.value?.photoColumns || []).map((column, index) => ({
    field: `photo${index + 1}`,
    column,
    label: `图${index + 1}`,
  }))
);
const photoCachePrefix = computed(() => `inspection:${templateConfig.value?.id}:`);
const photoCacheKey = (item, field) => `${photoCachePrefix.value}${item.id}:${field}`;

fetch(`${process.env.BASE_URL}data.json`)
  .then((res) => {
    if (!res.ok) throw new Error("巡检数据读取失败");
    return res.json();
  })
  .then((data) => {
    if (data.schemaVersion !== 2 || !data.template || !Array.isArray(data.items)) {
      throw new Error("模板数据版本不匹配，请重新生成 data.json");
    }
    templateConfig.value = data.template;
    originalData.value = data.items;
    // 刷新 tableData 时同步更新
    tableData.value = getAutoTableData();
    restoreSavedResults(true);
  })
  .catch((error) => {
    console.error("获取 data.json 失败", error);
    alert(error.message);
  });

function isPhotoOnlyItem(item) {
  return !item.resultOptions?.length;
}

// 自动填充结果/图片字段
function getAutoTableData() {
  return originalData.value.map((item) => ({
    ...item,
    result: "",
    ...Object.fromEntries(photoSlots.value.map(({ field }) => [field, ""])),
  }));
}

const tableData = ref(getAutoTableData());
const summaryText = ref("");
const hasRestoredFromStorage = ref(false);

const exporting = ref(false);
const exportingKind = ref("");
const exportProgress = ref(0);
const exportStatusText = ref("");
let exportResetTimer = null;

const backuping = ref(false);
const backupProgress = ref(0);
const backupStatusText = ref("");
const backupFileInput = ref(null);
let backupResetTimer = null;

const anyBusy = computed(() => exporting.value || backuping.value);
const progressVisible = computed(
  () =>
    exporting.value ||
    backuping.value ||
    exportProgress.value > 0 ||
    backupProgress.value > 0
);
const currentProgress = computed(() =>
  backuping.value || backupProgress.value > 0
    ? backupProgress.value
    : exportProgress.value
);
const currentStatusText = computed(() =>
  backuping.value || backupProgress.value > 0
    ? backupStatusText.value
    : exportStatusText.value
);

const LOCAL_STORAGE_PREFIX = computed(() => `camera-watermark:${templateConfig.value?.id}:`);
const RESULTS_STORAGE_KEY = computed(() => `${LOCAL_STORAGE_PREFIX.value}inspection:results`);
const SUMMARY_STORAGE_KEY = computed(() => `${LOCAL_STORAGE_PREFIX.value}inspection:summary`);

const loadSavedResults = () => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(RESULTS_STORAGE_KEY.value);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const persistResults = (resultsMap) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      RESULTS_STORAGE_KEY.value,
      JSON.stringify(resultsMap)
    );
  } catch {
    // ignore quota / private mode errors
  }
};

const loadSavedSummary = () => {
  if (typeof window === "undefined") return "";
  try {
    return window.localStorage.getItem(SUMMARY_STORAGE_KEY.value) || "";
  } catch {
    return "";
  }
};

const persistSummary = (value) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SUMMARY_STORAGE_KEY.value, value || "");
  } catch {
    // ignore quota / private mode errors
  }
};

const restoreSavedResults = (overwrite = false) => {
  const saved = loadSavedResults();
  if (!tableData.value.length) return;

  for (const row of tableData.value) {
    if (isPhotoOnlyItem(row)) {
      row.result = "";
      continue;
    }
    const savedValue = saved[row.id];
    if ((overwrite || !row.result) && row.resultOptions.includes(savedValue)) {
      row.result = savedValue;
    }
  }

  summaryText.value = loadSavedSummary();
  hasRestoredFromStorage.value = true;
};

const getResultSelectClass = (result) => {
  if (["合格", "已拍"].includes(result)) return "result-select-pass";
  if (["不合格", "未拍"].includes(result)) return "result-select-fail";
  if (result === "不涉及") return "result-select-na";
  return "";
};

const resultsSnapshot = computed(() =>
  tableData.value.map((row) => ({ id: row.id, result: row.result }))
);

watch(
  resultsSnapshot,
  () => {
    if (!hasRestoredFromStorage.value) return;
    const map = {};
    for (const row of tableData.value) {
      if (!isPhotoOnlyItem(row) && row.result) map[row.id] = row.result;
    }
    persistResults(map);
  },
  { deep: true }
);

watch(summaryText, (value) => {
  if (!hasRestoredFromStorage.value) return;
  persistSummary(value);
});

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () =>
      reject(reader.error || new Error("Blob 转 DataURL 失败"));
    reader.readAsDataURL(blob);
  });

const dataUrlToBlob = async (dataUrl) => {
  const res = await fetch(dataUrl);
  return await res.blob();
};

const dumpAppLocalStorage = () => {
  const out = {};
  if (typeof window === "undefined") return out;
  try {
    const storage = window.localStorage;
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (!key || !key.startsWith(LOCAL_STORAGE_PREFIX.value)) continue;
      const value = storage.getItem(key);
      if (typeof value === "string") out[key] = value;
    }
  } catch {
    // ignore
  }
  return out;
};

const clearAppLocalStorage = () => {
  if (typeof window === "undefined") return;
  try {
    const storage = window.localStorage;
    const keysToRemove = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && key.startsWith(LOCAL_STORAGE_PREFIX.value)) keysToRemove.push(key);
    }
    for (const key of keysToRemove) storage.removeItem(key);
  } catch {
    // ignore
  }
};

const extractDataUrl = (value) => {
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && typeof value.dataUrl === "string") {
    return value.dataUrl;
  }
  return "";
};

const handleExportBackup = async () => {
  if (anyBusy.value) return;

  backuping.value = true;
  backupProgress.value = 0;
  backupStatusText.value = "准备导出备份...";
  if (backupResetTimer) {
    clearTimeout(backupResetTimer);
    backupResetTimer = null;
  }

  try {
    backupStatusText.value = "读取本地缓存...";
    const localStorageDump = dumpAppLocalStorage();
    backupProgress.value = 10;

    backupStatusText.value = "读取图片缓存...";
    const entries = (await idbEntries()).filter(({ key }) =>
      String(key).startsWith(photoCachePrefix.value)
    );
    const images = {};
    const total = entries.length;
    let processed = 0;
    for (const { key, value } of entries) {
      processed += 1;
      if (value instanceof Blob) {
        const dataUrl = await blobToDataUrl(value);
        images[String(key)] = {
          dataUrl,
          type: value.type || "",
          size: value.size || 0,
        };
      } else if (typeof value === "string" && value.startsWith("data:")) {
        images[String(key)] = { dataUrl: value };
      }

      backupStatusText.value = `处理图片 ${processed}/${total}...`;
      backupProgress.value = Math.min(
        90,
        10 + Math.round((processed / Math.max(1, total)) * 80)
      );
    }

    backupStatusText.value = "生成文件...";
    backupProgress.value = 95;

    const backup = {
      schemaVersion: 2,
      templateId: templateConfig.value.id,
      app: "camera-watermark",
      exportedAt: new Date().toISOString(),
      localStorage: localStorageDump,
      indexedDB: {
        name: DB_NAME,
        version: DB_VERSION,
        stores: {
          [STORE_NAME]: images,
        },
      },
    };

    const json = JSON.stringify(backup);
    backupProgress.value = 100;
    saveAs(
      new Blob([json], { type: "application/json;charset=utf-8" }),
      `camera-watermark_backup_${dayjs().format("YYYYMMDD_HHmmss")}.json`
    );
  } catch (error) {
    console.error(error);
    alert("导出备份失败: " + (error?.message || String(error)));
  } finally {
    backuping.value = false;
    backupResetTimer = setTimeout(() => {
      backupProgress.value = 0;
      backupStatusText.value = "";
      backupResetTimer = null;
    }, 800);
  }
};

const triggerImportBackup = () => {
  if (anyBusy.value) return;
  const el = backupFileInput.value;
  if (!el) return;
  // 允许重复选择同一个文件
  el.value = "";
  el.click();
};

const handleImportBackupFile = async (event) => {
  const file = event?.target?.files?.[0];
  if (!file) return;
  // 允许重复选择同一个文件
  event.target.value = "";

  backuping.value = true;
  backupProgress.value = 0;
  backupStatusText.value = "读取备份文件...";
  if (backupResetTimer) {
    clearTimeout(backupResetTimer);
    backupResetTimer = null;
  }

  try {
    const text = await file.text();
    backupProgress.value = 10;
    backupStatusText.value = "解析备份内容...";

    const parsed = JSON.parse(text);
    if (!parsed || typeof parsed !== "object") {
      throw new Error("备份文件格式不正确");
    }

    if (parsed.schemaVersion !== 2 || parsed.templateId !== templateConfig.value.id) {
      throw new Error("备份不属于当前巡检模板，无法导入；现有数据未更改");
    }

    const ok = confirm(
      "导入会覆盖当前模板已保存的检查结果和照片，是否继续？"
    );
    if (!ok) return;

    backupStatusText.value = "清空本地缓存...";
    backupProgress.value = 15;
    clearAppLocalStorage();
    for (const { key } of await idbEntries()) {
      if (String(key).startsWith(photoCachePrefix.value)) await idbDel(key);
    }

    backupStatusText.value = "写入本地缓存...";
    const localStorageDump =
      parsed.localStorage && typeof parsed.localStorage === "object"
        ? parsed.localStorage
        : {};

    for (const [key, value] of Object.entries(localStorageDump)) {
      if (!key.startsWith(LOCAL_STORAGE_PREFIX.value)) continue;
      try {
        window.localStorage.setItem(
          key,
          typeof value === "string" ? value : JSON.stringify(value)
        );
      } catch {
        // ignore quota / private mode errors
      }
    }
    backupProgress.value = 25;

    // 先恢复检查结果（UI + localStorage）
    const restoredResults = loadSavedResults();
    for (const row of tableData.value) {
      if (isPhotoOnlyItem(row)) {
        row.result = "";
        continue;
      }
      const v = restoredResults?.[row.id] ?? restoredResults?.[String(row.id)];
      row.result = row.resultOptions.includes(v) ? v : "";
    }
    summaryText.value = loadSavedSummary();

    // 写入图片缓存（IndexedDB），并同步 UI
    backupStatusText.value = "写入图片缓存...";
    const storeDump =
      parsed?.indexedDB?.stores?.[STORE_NAME] ||
      parsed?.indexedDB?.[STORE_NAME] ||
      {};

    const imageEntries =
      storeDump && typeof storeDump === "object"
        ? Object.entries(storeDump).filter(([key]) => key.startsWith(photoCachePrefix.value))
        : [];

    const total = imageEntries.length;
    let processed = 0;

    // 先清空 UI 中的图片，避免残留
    for (const row of tableData.value) {
      for (const { field } of photoSlots.value) row[field] = "";
    }

    for (const [key, value] of imageEntries) {
      const dataUrl = extractDataUrl(value);
      if (!dataUrl) continue;

      processed += 1;
      const blob = await dataUrlToBlob(dataUrl);
      await idbSet(key, blob);

      const match = /^(\d+):(photo\d+)$/.exec(key.slice(photoCachePrefix.value.length));
      if (match) {
        const id = Number(match[1]);
        const field = match[2];
        const row = tableData.value.find((r) => r.id === id);
        if (row && photoSlots.value.some((photo) => photo.field === field)) row[field] = dataUrl;
      }

      backupStatusText.value = `写入图片 ${processed}/${total}...`;
      backupProgress.value = Math.min(
        95,
        25 + Math.round((processed / Math.max(1, total)) * 70)
      );
    }

    backupStatusText.value = "导入完成";
    backupProgress.value = 100;
    alert("导入完成");
  } catch (error) {
    console.error(error);
    alert("导入备份失败: " + (error?.message || String(error)));
  } finally {
    backuping.value = false;
    backupResetTimer = setTimeout(() => {
      backupProgress.value = 0;
      backupStatusText.value = "";
      backupResetTimer = null;
    }, 800);
  }
};

// 图片处理工具
const getImageInfo = (base64) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () =>
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = reject;
    img.src = base64;
  });
};

const calculateImageSize = (originalW, originalH, maxW, maxH) => {
  const safeW = Math.max(1, originalW);
  const safeH = Math.max(1, originalH);
  const safeMaxW = Math.max(1, maxW);
  const safeMaxH = Math.max(1, maxH);

  const scale = Math.min(safeMaxW / safeW, safeMaxH / safeH);
  return {
    width: Math.max(1, Math.floor(safeW * scale)),
    height: Math.max(1, Math.floor(safeH * scale)),
  };
};

const DEFAULT_COL_WIDTH_CHARS = 8.43;
const DEFAULT_ROW_HEIGHT_POINTS = 15;

const columnWidthToPixels = (widthChars) => {
  const w = Number(widthChars);
  const width = Number.isFinite(w) && w > 0 ? w : DEFAULT_COL_WIDTH_CHARS;
  return Math.max(1, Math.floor(width * 7 + 5));
};

const rowHeightToPixels = (heightPoints) => {
  const h = Number(heightPoints);
  const points = Number.isFinite(h) && h > 0 ? h : DEFAULT_ROW_HEIGHT_POINTS;
  return Math.max(1, Math.floor((points * 96) / 72));
};

const getCellOrMergedCellMaxSizePx = (
  worksheet,
  excelRowNumber,
  colZeroBased
) => {
  const FILL_RATIO = 0.95;
  const colNumber = colZeroBased + 1; // worksheet.getCell/getColumn 是 1-based
  const merges = worksheet?._merges;

  const cell = worksheet.getCell(excelRowNumber, colNumber);
  const master = cell?.master || cell;
  const mergeDimensions = merges?.[master?.address];

  const defaultColWidth =
    worksheet?.properties?.defaultColWidth ?? DEFAULT_COL_WIDTH_CHARS;
  const defaultRowHeight =
    worksheet?.properties?.defaultRowHeight ?? DEFAULT_ROW_HEIGHT_POINTS;

  if (mergeDimensions) {
    let maxW = 0;
    for (let c = mergeDimensions.left; c <= mergeDimensions.right; c++) {
      const column = worksheet.getColumn(c);
      const widthChars = column?.width ?? defaultColWidth;
      maxW += columnWidthToPixels(widthChars);
    }

    let maxH = 0;
    for (let r = mergeDimensions.top; r <= mergeDimensions.bottom; r++) {
      const row = worksheet.getRow(r);
      const heightPoints = row?.height ?? defaultRowHeight;
      maxH += rowHeightToPixels(heightPoints);
    }

    return {
      maxW: Math.max(1, Math.floor(maxW * FILL_RATIO)),
      maxH: Math.max(1, Math.floor(maxH * FILL_RATIO)),
    };
  }

  const column = worksheet.getColumn(colNumber);
  const row = worksheet.getRow(excelRowNumber);

  const widthChars = column?.width ?? defaultColWidth;
  const heightPoints = row?.height ?? defaultRowHeight;

  return {
    maxW: Math.max(1, Math.floor(columnWidthToPixels(widthChars) * FILL_RATIO)),
    maxH: Math.max(1, Math.floor(rowHeightToPixels(heightPoints) * FILL_RATIO)),
  };
};

const normalizeArgb = (argb) => {
  if (!argb) return null;
  const hex = String(argb).replace(/^#/, "").toUpperCase();
  if (/^[0-9A-F]{8}$/.test(hex)) return hex;
  if (/^[0-9A-F]{6}$/.test(hex)) return `FF${hex}`;
  return null;
};

const argbToCssColor = (argb, fallback = "") => {
  const hex = normalizeArgb(argb);
  if (!hex) return fallback;
  const alpha = parseInt(hex.slice(0, 2), 16) / 255;
  const red = parseInt(hex.slice(2, 4), 16);
  const green = parseInt(hex.slice(4, 6), 16);
  const blue = parseInt(hex.slice(6, 8), 16);
  if (alpha >= 0.999) return `rgb(${red}, ${green}, ${blue})`;
  return `rgba(${red}, ${green}, ${blue}, ${alpha.toFixed(3)})`;
};

const getCellPlainText = (value) => {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  if (value instanceof Date) {
    return dayjs(value).format("YYYY-MM-DD HH:mm:ss");
  }

  if (typeof value === "object") {
    if (Array.isArray(value.richText)) {
      return value.richText.map((part) => part?.text || "").join("");
    }
    if (typeof value.text === "string") return value.text;
    if (typeof value.result === "string" || typeof value.result === "number") {
      return String(value.result);
    }
  }

  return String(value);
};

const wrapTextLines = (context, text, maxWidth) => {
  const safeWidth = Number.isFinite(maxWidth) ? Math.max(1, maxWidth) : 1;
  if (!text) return [""];

  const paragraphs = String(text).split(/\r?\n/);
  const lines = [];
  for (const paragraph of paragraphs) {
    if (!paragraph) {
      lines.push("");
      continue;
    }

    let current = "";
    let currentWidth = 0;
    for (const character of paragraph) {
      const characterWidth = context.measureText(character).width;
      if (!current || currentWidth + characterWidth <= safeWidth) {
        current += character;
        currentWidth += characterWidth;
      } else {
        lines.push(current);
        current = character;
        currentWidth = characterWidth;
      }
    }
    if (current) lines.push(current);
  }

  return lines.length ? lines : [""];
};

const buildWorksheetRenderModel = (worksheet) => {
  const dimensions = worksheet?.dimensions;
  const top = dimensions?.top ?? 1;
  const left = dimensions?.left ?? 1;
  const bottom =
    dimensions?.bottom ?? Math.max(worksheet?.rowCount || 1, 1);
  const right =
    dimensions?.right ?? Math.max(worksheet?.columnCount || 1, 1);

  const defaultColWidth =
    worksheet?.properties?.defaultColWidth ?? DEFAULT_COL_WIDTH_CHARS;
  const defaultRowHeight =
    worksheet?.properties?.defaultRowHeight ?? DEFAULT_ROW_HEIGHT_POINTS;

  const colWidthsPx = new Array(right + 1).fill(0);
  const colLeftPx = new Array(right + 2).fill(0);
  for (let column = left; column <= right; column += 1) {
    const widthChars =
      worksheet.getColumn(column)?.width ?? defaultColWidth;
    colWidthsPx[column] = columnWidthToPixels(widthChars);
  }
  for (let column = left + 1; column <= right + 1; column += 1) {
    colLeftPx[column] =
      colLeftPx[column - 1] + (colWidthsPx[column - 1] || 0);
  }

  const rowHeightsPx = new Array(bottom + 1).fill(0);
  const rowTopPx = new Array(bottom + 2).fill(0);
  for (let row = top; row <= bottom; row += 1) {
    const heightPoints = worksheet.getRow(row)?.height ?? defaultRowHeight;
    rowHeightsPx[row] = rowHeightToPixels(heightPoints);
  }
  for (let row = top + 1; row <= bottom + 1; row += 1) {
    rowTopPx[row] = rowTopPx[row - 1] + (rowHeightsPx[row - 1] || 0);
  }

  const sheetWidthPx = colLeftPx[right + 1] - colLeftPx[left];
  const sheetHeightPx = rowTopPx[bottom + 1] - rowTopPx[top];

  const merges = worksheet?._merges || {};
  const processed = new Set();
  const items = [];
  const measureCanvas = document.createElement("canvas");
  const measureContext = measureCanvas.getContext("2d");

  if (!measureContext) {
    throw new Error("当前浏览器不支持 Canvas，无法导出 PDF");
  }

  const fallbackFonts =
    '-apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Microsoft YaHei", Arial, sans-serif';
  const padding = 4;

  for (let row = top; row <= bottom; row += 1) {
    for (let column = left; column <= right; column += 1) {
      const cell = worksheet.getCell(row, column);
      const master = cell?.master || cell;
      if (!master || processed.has(master.address)) continue;
      processed.add(master.address);

      const merge = merges?.[master.address];
      const range = merge
        ? {
            top: merge.top,
            left: merge.left,
            bottom: merge.bottom,
            right: merge.right,
          }
        : { top: row, left: column, bottom: row, right: column };

      const x = colLeftPx[range.left];
      const y = rowTopPx[range.top];
      const width = colLeftPx[range.right + 1] - colLeftPx[range.left];
      const height = rowTopPx[range.bottom + 1] - rowTopPx[range.top];

      const font = master.font || {};
      const fontName = font.name
        ? `"${font.name}", ${fallbackFonts}`
        : fallbackFonts;
      const fontSizePt = Number(font.size) || 11;
      const fontSizePx = Math.max(8, Math.round((fontSizePt * 96) / 72));
      const fontStyle = font.italic ? "italic" : "normal";
      const fontWeight = font.bold ? "bold" : "normal";

      const alignment = master.alignment || {};
      const horizontal = alignment.horizontal || "left";
      const vertical = alignment.vertical || "bottom";
      const text = getCellPlainText(master.value);

      let lines = [];
      let fittedFontSizePx = fontSizePx;
      let fontString = `${fontStyle} ${fontWeight} ${fontSizePx}px ${fontName}`;
      if (text) {
        const maxTextWidth = Math.max(1, width - padding * 2);
        for (
          let candidateSize = fontSizePx;
          candidateSize >= 8;
          candidateSize -= 1
        ) {
          const candidateFont = `${fontStyle} ${fontWeight} ${candidateSize}px ${fontName}`;
          measureContext.font = candidateFont;
          const candidateLines = alignment.wrapText
            ? wrapTextLines(measureContext, text, maxTextWidth)
            : text.split(/\r?\n/);
          const lineHeight = Math.max(10, Math.round(candidateSize * 1.25));
          const fitsHeight =
            candidateLines.length * lineHeight <= height - padding * 2;

          fittedFontSizePx = candidateSize;
          fontString = candidateFont;
          lines = candidateLines;
          if (!alignment.wrapText || fitsHeight) break;
        }
      }

      const fill = master.fill;
      const fillColor =
        fill?.type === "pattern" &&
        fill?.pattern === "solid" &&
        fill?.fgColor?.argb
          ? argbToCssColor(fill.fgColor.argb)
          : "";

      items.push({
        rect: { x, y, width, height },
        fillColor,
        border: master.border || {},
        text: lines,
        align: { horizontal, vertical },
        font: {
          string: fontString,
          sizePx: fittedFontSizePx,
          color: argbToCssColor(font?.color?.argb, "rgb(0, 0, 0)"),
        },
        padding,
      });
    }
  }

  return {
    range: { top, left, bottom, right },
    sheetWidthPx,
    sheetHeightPx,
    items,
    colLeftPx,
    rowTopPx,
  };
};

const renderWorksheetToPdf = async ({ worksheet, images, filename }) => {
  const model = buildWorksheetRenderModel(worksheet);
  const { sheetWidthPx, sheetHeightPx, items, colLeftPx, rowTopPx } =
    model;

  const loadedImages = await Promise.all(
    (images || []).map(
      (image) =>
        new Promise((resolve) => {
          const element = new Image();
          element.onload = () => {
            const columnNumber = (image.col0 ?? 0) + 1;
            const rowNumber = (image.row0 ?? 0) + 1;
            resolve({
              element,
              x: colLeftPx[columnNumber] || 0,
              y: rowTopPx[rowNumber] || 0,
              width: image.w || 0,
              height: image.h || 0,
            });
          };
          element.onerror = () => resolve(null);
          element.src = image.dataUrl;
        })
    )
  );
  const readyImages = loadedImages.filter(Boolean);

  const pdf = new jsPDF(worksheet.pageSetup.orientation === "landscape" ? "l" : "p", "mm", "a4");
  const marginMm = 6;
  const pageWidthMm = pdf.internal.pageSize.getWidth();
  const pageHeightMm = pdf.internal.pageSize.getHeight();
  const contentWidthMm = pageWidthMm - marginMm * 2;
  const contentHeightMm = pageHeightMm - marginMm * 2;
  const maxSliceHeightPx = Math.max(
    1,
    Math.floor((contentHeightMm / contentWidthMm) * sheetWidthPx)
  );

  const renderScale = Math.min(
    2,
    Math.max(1, window.devicePixelRatio || 1)
  );
  const slices = [];
  const topRow = model.range.top;
  const bottomRow = model.range.bottom;

  let startRow = topRow;
  let yStart = rowTopPx[startRow] || 0;
  while (startRow <= bottomRow) {
    let endRow = startRow;
    let yEnd = rowTopPx[endRow + 1] ?? sheetHeightPx;
    while (
      endRow < bottomRow &&
      (rowTopPx[endRow + 2] ?? sheetHeightPx) - yStart <=
        maxSliceHeightPx
    ) {
      endRow += 1;
      yEnd = rowTopPx[endRow + 1] ?? sheetHeightPx;
    }

    slices.push({ yStart, sliceHeightPx: yEnd - yStart });
    startRow = endRow + 1;
    yStart = yEnd;
  }

  const totalPages = slices.length || 1;

  for (let pageIndex = 0; pageIndex < slices.length; pageIndex += 1) {
    const { yStart: sliceTop, sliceHeightPx } = slices[pageIndex];

    exportStatusText.value = `生成 PDF 第 ${pageIndex + 1}/${totalPages} 页...`;
    exportProgress.value = Math.max(
      exportProgress.value,
      Math.min(99, 70 + Math.round(((pageIndex + 1) / totalPages) * 29))
    );

    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.floor(sheetWidthPx * renderScale));
    canvas.height = Math.max(1, Math.floor(sliceHeightPx * renderScale));
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("当前浏览器不支持 Canvas，无法导出 PDF");
    }

    context.fillStyle = "#FFFFFF";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.setTransform(
      renderScale,
      0,
      0,
      renderScale,
      0,
      -sliceTop * renderScale
    );

    const sliceBottom = sliceTop + sliceHeightPx;

    for (const item of items) {
      const { x, y, width, height } = item.rect;
      if (y + height < sliceTop || y > sliceBottom) continue;

      if (item.fillColor) {
        context.fillStyle = item.fillColor;
        context.fillRect(x, y, width, height);
      }

      const drawSide = (side, x1, y1, x2, y2) => {
        if (!side?.style) return;
        context.strokeStyle = argbToCssColor(
          side.color?.argb,
          "rgb(0, 0, 0)"
        );
        context.lineWidth =
          side.style === "thick" ? 2.5 : side.style === "medium" ? 2 : 1;
        context.beginPath();
        context.moveTo(x1, y1);
        context.lineTo(x2, y2);
        context.stroke();
      };

      const border = item.border || {};
      drawSide(border.top, x, y, x + width, y);
      drawSide(border.bottom, x, y + height, x + width, y + height);
      drawSide(border.left, x, y, x, y + height);
      drawSide(border.right, x + width, y, x + width, y + height);

      if (item.text?.some((line) => line)) {
        const { horizontal, vertical } = item.align || {};
        const padding = item.padding ?? 4;
        const fontSizePx = item.font?.sizePx ?? 12;
        const lineHeight = Math.max(10, Math.round(fontSizePx * 1.25));
        const textHeight = item.text.length * lineHeight;
        const visibleTop = Math.max(y, sliceTop);
        const visibleBottom = Math.min(y + height, sliceBottom);
        const visibleHeight = Math.max(1, visibleBottom - visibleTop);

        let textY = visibleTop + padding;
        if (vertical === "middle" || vertical === "center") {
          textY =
            visibleTop +
            Math.max(padding, Math.floor((visibleHeight - textHeight) / 2));
        } else if (vertical === "bottom") {
          textY =
            visibleTop +
            Math.max(padding, visibleHeight - padding - textHeight);
        }

        let textAlign = "left";
        let textX = x + padding;
        if (horizontal === "center" || horizontal === "centerContinuous") {
          textAlign = "center";
          textX = x + width / 2;
        } else if (horizontal === "right") {
          textAlign = "right";
          textX = x + width - padding;
        }

        context.save();
        context.beginPath();
        context.rect(x, visibleTop, width, visibleHeight);
        context.clip();
        context.font =
          item.font?.string || `normal normal ${fontSizePx}px sans-serif`;
        context.fillStyle = item.font?.color || "rgb(0, 0, 0)";
        context.textAlign = textAlign;
        context.textBaseline = "top";

        for (let lineIndex = 0; lineIndex < item.text.length; lineIndex += 1) {
          const line = item.text[lineIndex];
          if (!line) continue;
          context.fillText(line, textX, textY + lineIndex * lineHeight);
        }
        context.restore();
      }
    }

    for (const image of readyImages) {
      const imageBottom = image.y + image.height;
      if (imageBottom < sliceTop || image.y > sliceBottom) continue;
      context.drawImage(
        image.element,
        image.x,
        image.y,
        image.width,
        image.height
      );
    }

    const imageData = canvas.toDataURL("image/jpeg", 0.95);
    const imageHeightMm =
      (sliceHeightPx / sheetWidthPx) * contentWidthMm;

    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(
      imageData,
      "JPEG",
      marginMm,
      marginMm,
      contentWidthMm,
      imageHeightMm
    );
  }

  exportProgress.value = 100;
  pdf.save(filename);
};

const handleExportExcel = async () => {
  if (
    tableData.value.some(
      (row) => !isPhotoOnlyItem(row) && !row.result
    )
  ) {
    if (!confirm("还有项目未选择结果，确定要导出吗？")) return;
  }

  exporting.value = true;
  exportingKind.value = "excel";
  exportProgress.value = 0;
  exportStatusText.value = "准备导出...";
  if (exportResetTimer) {
    clearTimeout(exportResetTimer);
    exportResetTimer = null;
  }
  try {
    const { workbook } = await buildInspectionWorkbook();

    exportStatusText.value = "生成文件...";
    exportProgress.value = 95;
    const outBuffer = await workbook.xlsx.writeBuffer();
    const exportBuffer = await restoreTemplateDefaultFont(outBuffer);
    exportProgress.value = 100;
    saveAs(
      new Blob([exportBuffer]),
      `巡检报告_${dayjs().format("YYYYMMDD")}.xlsx`
    );
  } catch (error) {
    console.error(error);
    alert("导出失败: " + error.message);
  } finally {
    exporting.value = false;
    exportingKind.value = "";
    exportResetTimer = setTimeout(() => {
      exportProgress.value = 0;
      exportStatusText.value = "";
      exportResetTimer = null;
    }, 800);
  }
};

// ExcelJS serializes the workbook Normal style with Calibri. The template
// uses SimSun, which affects the physical width used for wrapped Chinese text.
const restoreTemplateDefaultFont = async (buffer) => {
  const zip = await JSZip.loadAsync(buffer);
  const stylesFile = zip.file("xl/styles.xml");
  if (!stylesFile) return buffer;

  const stylesXml = await stylesFile.async("string");
  const fontsBlock = stylesXml.match(/<fonts\b[^>]*>([\s\S]*?)<\/fonts>/);
  const fontNodes = fontsBlock
    ? [...fontsBlock[1].matchAll(/<font>([\s\S]*?)<\/font>/g)]
    : [];
  const templateFontId = fontNodes.findIndex(
    ([, fontXml]) =>
      /<name\s+val="宋体"\s*\/?>(?:<\/name>)?/.test(fontXml) &&
      /<sz\s+val="11"\s*\/?>(?:<\/sz>)?/.test(fontXml)
  );
  if (templateFontId < 0) return buffer;

  const patchedStylesXml = stylesXml.replace(
    /(<cellStyleXfs\b[^>]*>\s*)<xf\b([^>]*?)\/>/,
    (_, prefix, attrs) => {
      const withoutFont = attrs.replace(/\sfontId="[^"]*"/, "");
      return `${prefix}<xf${withoutFont} fontId="${templateFontId}"/>`;
    }
  );
  if (patchedStylesXml === stylesXml) return buffer;

  zip.file("xl/styles.xml", patchedStylesXml);
  return zip.generateAsync({ type: "arraybuffer" });
};

const buildInspectionWorkbook = async () => {
  exportStatusText.value = "读取模板...";
  const response = await fetch(`${process.env.BASE_URL}${templateConfig.value.file}`);
  if (!response.ok) throw new Error("模板文件读取失败");
  const buffer = await response.arrayBuffer();
  exportProgress.value = 5;

  const workbook = new ExcelJS.Workbook();
  exportStatusText.value = "解析模板...";
  await workbook.xlsx.load(buffer);
  const worksheet = workbook.getWorksheet(templateConfig.value.sheetName);
  if (!worksheet) throw new Error("模板工作表与巡检数据不匹配，请重新生成 data.json");
  exportProgress.value = 10;

  // Keep the template's existing style objects and only fill a missing wrap
  // flag, so narrow Chinese columns continue to wrap in future templates.
  worksheet.eachRow({ includeEmpty: false }, (templateRow) => {
    templateRow.eachCell({ includeEmpty: false }, (cell) => {
      if (cell.value === null || cell.value === undefined) return;
      if (cell.alignment?.wrapText === true) return;
      cell.alignment = { ...(cell.alignment || {}), wrapText: true };
    });
  });

  const pdfImages = [];

  const total = tableData.value.length;
  let processed = 0;
  for (const item of tableData.value) {
    processed += 1;
    exportStatusText.value = `正在写入数据 ${processed}/${total}...`;
    const row = worksheet.getRow(item.excelRow);

    // Column positions and row-specific options come from the template headers.
    if (!isPhotoOnlyItem(item)) {
      const resultCell = row.getCell(templateConfig.value.resultColumn);
      resultCell.value = item.result;
      resultCell.alignment = {
        ...(resultCell.alignment || {}),
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };

      const baseFont = resultCell.font || {};
      if (["不合格", "未拍"].includes(item.result)) {
        resultCell.font = {
          ...baseFont,
          color: { argb: "FFFF0000" },
          bold: true,
        };
      } else if (item.result) {
        resultCell.font = {
          ...baseFont,
          color: { argb: "FF000000" },
        };
      }
    }

    const photos = photoSlots.value.map(({ field, column }) => ({
      data: item[field], col: column - 1,
    }));

    for (const p of photos) {
      if (p.data) {
        const { width: rawW, height: rawH } = await getImageInfo(p.data);
        const { maxW, maxH } = getCellOrMergedCellMaxSizePx(
          worksheet,
          item.excelRow,
          p.col
        );
        const size = calculateImageSize(rawW, rawH, maxW, maxH);

        const match = p.data.match(/^data:image\/(\w+);base64,/);
        let extension = (match?.[1] || "png").toLowerCase();
        if (extension === "jpg") extension = "jpeg";
        const imageId = workbook.addImage({
          base64: p.data.replace(/^data:image\/\w+;base64,/, ""),
          extension,
        });

        worksheet.addImage(imageId, {
          tl: { col: p.col, row: item.excelRow - 1 },
          ext: size,
          editAs: "oneCell",
        });

        pdfImages.push({
          dataUrl: p.data,
          col0: p.col,
          row0: item.excelRow - 1,
          w: size.width,
          h: size.height,
        });
      }
    }

    exportProgress.value = Math.min(
      90,
      10 + Math.round((processed / total) * 80)
    );
  }

  exportStatusText.value = "写入汇总...";
  const { summaryTitleRange, summaryContentRange } = templateConfig.value;
  const summaryTitleCell = worksheet.getCell(summaryTitleRange.split(":")[0]);
  const summaryContentCell = worksheet.getCell(summaryContentRange.split(":")[0]);
  const summaryRowNumber = summaryTitleCell.row;
  const summaryAlignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true,
  };
  const summaryBorder = {
    top: { style: "thin", color: { argb: "FF000000" } },
    bottom: { style: "thin", color: { argb: "FF000000" } },
    left: { style: "thin", color: { argb: "FF000000" } },
    right: { style: "thin", color: { argb: "FF000000" } },
  };

  worksheet.mergeCells(summaryTitleRange);
  worksheet.mergeCells(summaryContentRange);
  worksheet.getRow(summaryRowNumber).height = 45;

  summaryTitleCell.value = "检查结果汇总";
  summaryTitleCell.alignment = summaryAlignment;
  summaryTitleCell.border = summaryBorder;
  summaryTitleCell.font = { ...worksheet.getCell("A6").font, bold: true };

  summaryContentCell.value = summaryText.value;
  summaryContentCell.alignment = summaryAlignment;
  summaryContentCell.border = summaryBorder;
  summaryContentCell.font = { ...worksheet.getCell("D6").font };

  worksheet.pageSetup = {
    ...worksheet.pageSetup,
    orientation: "landscape",
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    printArea: `A1:${worksheet.getColumn(Math.max(...templateConfig.value.photoColumns)).letter}${summaryRowNumber}`,
  };

  exportProgress.value = 92;

  return { workbook, worksheet, pdfImages };
};

const handleExportPdf = async () => {
  if (
    tableData.value.some(
      (row) => !isPhotoOnlyItem(row) && !row.result
    )
  ) {
    if (!confirm("还有项目未选择结果，确定要导出吗？")) return;
  }

  exporting.value = true;
  exportingKind.value = "pdf";
  exportProgress.value = 0;
  exportStatusText.value = "准备导出 PDF...";
  if (exportResetTimer) {
    clearTimeout(exportResetTimer);
    exportResetTimer = null;
  }

  try {
    const { worksheet, pdfImages } = await buildInspectionWorkbook();

    exportStatusText.value = "生成 PDF...";
    exportProgress.value = Math.max(exportProgress.value, 70);

    await renderWorksheetToPdf({
      worksheet,
      images: pdfImages,
      filename: `巡检报告_${dayjs().format("YYYYMMDD")}.pdf`,
    });
  } catch (error) {
    console.error(error);
    alert("导出失败: " + error.message);
  } finally {
    exporting.value = false;
    exportingKind.value = "";
    exportResetTimer = setTimeout(() => {
      exportProgress.value = 0;
      exportStatusText.value = "";
      exportResetTimer = null;
    }, 800);
  }
};

onMounted(() => {
  restoreSavedResults();
});
</script>

<style scoped>
:deep(.result-select-pass .el-select__selected-item) {
  color: #16a34a;
  font-weight: 700;
}

:deep(.result-select-pass .el-input__wrapper) {
  background-color: #f0fdf4;
}

:deep(.result-select-fail .el-select__selected-item) {
  color: #ef4444;
  font-weight: 700;
}

:deep(.result-select-fail .el-input__wrapper) {
  background-color: #fef2f2;
}

:deep(.result-select-na .el-select__selected-item) {
  color: #6b7280;
  font-weight: 600;
}

:deep(.result-select-na .el-input__wrapper) {
  background-color: #f9fafb;
}
</style>
